import { Corner, OverlayDimensions } from "../types";
import { clamp } from "./clamp";

interface ComputeResizeInput {
  pointerXInContainer: number;
  pointerYInContainer: number;
  anchorPx: { x: number; y: number };
  overlayRatio: number;
  overlayDims: OverlayDimensions;
  rectWidth: number;
  rectHeight: number;
  minSizePercent: number;
  maxSizePercent: number;
  corner: Corner;
}

interface ComputeResizeResult {
  sizePercent: number;
  leftPx: number;
  topPx: number;
  widthPx: number;
  heightPx: number;
}

/**
 * Считает новый размер и top-left overlay'а при drag'е за corner-handle.
 * Сохраняет соотношение сторон (по оси, которая «увеличилась сильнее») и
 * якорит overlay в противоположном углу. После clamp'а размера пересчитывает
 * actual display-габариты — иначе позиция уехала бы в неконсистентное значение.
 */
export const computeResize = ({
  pointerXInContainer,
  pointerYInContainer,
  anchorPx,
  overlayRatio,
  overlayDims,
  rectWidth,
  rectHeight,
  minSizePercent,
  maxSizePercent,
  corner,
}: ComputeResizeInput): ComputeResizeResult => {
  const rawW = Math.abs(pointerXInContainer - anchorPx.x);
  const rawH = Math.abs(pointerYInContainer - anchorPx.y);

  // keepRatio: берём ту ось, что «увеличилась сильнее» относительно
  // соотношения сторон overlay'а — даёт привычный free-resize-фидбек.
  const candidateW = Math.max(rawW, rawH * overlayRatio);
  const candidateH = candidateW / overlayRatio;

  const minSidePx = Math.min(rectWidth, rectHeight);
  const candidateMaxDisplay = Math.max(candidateW, candidateH);
  const sizePercent = clamp(
    (candidateMaxDisplay / minSidePx) * 100,
    minSizePercent,
    maxSizePercent,
  );

  const actualMaxDisplay = (sizePercent / 100) * minSidePx;
  const overlayMaxNat = Math.max(overlayDims.w, overlayDims.h, 1);
  const scale = actualMaxDisplay / overlayMaxNat;
  const widthPx = overlayDims.w * scale;
  const heightPx = overlayDims.h * scale;

  // Top-left overlay'а после resize'а — рассчитан так, чтобы anchor (дальний
  // от handle'а угол) остался на месте.
  let leftPx = anchorPx.x;
  let topPx = anchorPx.y;
  if (corner === "nw") {
    leftPx = anchorPx.x - widthPx;
    topPx = anchorPx.y - heightPx;
  } else if (corner === "ne") {
    leftPx = anchorPx.x;
    topPx = anchorPx.y - heightPx;
  } else if (corner === "sw") {
    leftPx = anchorPx.x - widthPx;
    topPx = anchorPx.y;
  }

  return { sizePercent, leftPx, topPx, widthPx, heightPx };
};
